import { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Save, Loader2, Plus, Trash2, User, Upload, ChevronDown, ExternalLink, Users } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import RichTextEditor from '@/components/RichTextEditor';
import { API } from '@/App';

const ROLE_OPTIONS = [
  'Président(e)', 'Vice-Président(e)', 'Secrétaire', 'Secrétaire Général(e)',
  'Trésorier(ère)', 'Membre du bureau', 'Responsable Formation', 'Membre',
];

const RégionalPageManagement = () => {
  const [regions, setRégions] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [regionDetail, setRégionDetail] = useState(null);
  const [description, setDescription] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchRégions(); }, []);

  const fetchRégions = async () => {
    try {
      const res = await axios.get(`${API}/admin/regions`);
      setRégions(res.data || []);
      if (res.data.length > 0) {
        setSelectedSlug(res.data[0].slug);
        await loadRégion(res.data[0].slug);
      }
    } catch { toast.error('Erreur chargement régions'); }
    setLoading(false);
  };

  const loadRégion = async (slug) => {
    try {
      const res = await axios.get(`${API}/regions/${slug}`);
      const r = res.data;
      setRégionDetail(r);
      setDescription(r.description || '');
      const bureau = (r.bureau || []).map(m => ({
        name: m.name || '',
        role: m.role || 'Membre',
        photo_url: m.photo_url || '',
        email: m.email || '',
      }));
      setMembers(bureau.length > 0 ? bureau : (r.members || []).map(m => ({
        name: m.name || '',
        role: m.role || 'Membre',
        photo_url: m.photo_url || '',
        email: m.email || '',
      })));
    } catch { toast.error('Erreur chargement détails'); }
  };

  const handleRégionChange = (slug) => {
    setSelectedSlug(slug);
    loadRégion(slug);
  };

  const addMember = () => {
    setMembers(prev => [...prev, { name: '', role: 'Membre', photo_url: '', email: '' }]);
  };

  const removeMember = (i) => setMembers(prev => prev.filter((_, idx) => idx !== i));

  const updateMember = (i, field, val) => {
    setMembers(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: val } : m));
  };

  const handlePhotoUpload = async (i, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/cms-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateMember(i, 'photo_url', res.data.url);
      toast.success('Photo uploadée');
    } catch { toast.error("Erreur d'upload"); }
  };

  const handleSave = async () => {
    if (!selectedSlug) return;
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/admin/regions/${selectedSlug}`, { description }),
        axios.put(`${API}/admin/regions/${selectedSlug}/members`, { members }),
      ]);
      toast.success('Page régionale mise à jour !');
      await loadRégion(selectedSlug);
    } catch { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  const currentRégion = regions.find(r => r.slug === selectedSlug);

  if (loading) return <AdminLayout><div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-5xl mx-auto" data-testid="regional-management">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <MapPin size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Pages Régionales</h1>
              <p className="text-xs text-gray-500">Gérez la présentation et le bureau de chaque région</p>
            </div>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1e3a5f] hover:bg-[#162d4a] text-white text-sm font-bold disabled:opacity-50 transition-colors" data-testid="save-region-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Sauvegarder
          </button>
        </div>

        {/* Région Selector */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Sélectionner une région</label>
          <div className="relative">
            <select value={selectedSlug} onChange={e => handleRégionChange(e.target.value)}
              className="w-full pl-4 pr-10 py-3 rounded-xl border border-gray-200 text-sm font-semibold appearance-none bg-white focus:border-[#35AFE4] outline-none" data-testid="region-selector">
              {regions.map(r => (
                <option key={r.slug} value={r.slug}>{r.name} ({(r.bureau || r.members || []).length} membres)</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          {regionDetail && (
            <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
              {regionDetail.departments?.length > 0 && (
                <span>Départements : {regionDetail.departments.join(', ')}</span>
              )}
              <a href={`/region/${selectedSlug}`} target="_blank" rel="noreferrer" className="text-[#35AFE4] hover:underline font-semibold flex items-center gap-1">
                <ExternalLink size={11} /> Voir la page publique
              </a>
            </div>
          )}
        </div>

        {/* Presentation Section */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
          <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <MapPin size={14} className="text-[#35AFE4]" /> Présentation de la région
          </h2>
          <RichTextEditor value={description} onChange={setDescription} placeholder="Description de la région SFOALC..." height="150px" />
        </div>

        {/* Bureau Members */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-gray-700 flex items-center gap-2">
              <Users size={14} className="text-[#35AFE4]" /> Bureau régional ({members.length} membres)
            </h2>
            <button onClick={addMember} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#eaf6fc] text-[#35AFE4] rounded-lg text-xs font-semibold hover:bg-[#d5ebf2] transition-colors" data-testid="add-regional-member">
              <Plus size={14} /> Ajouter un membre
            </button>
          </div>

          <div className="space-y-3">
            {members.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Users size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Aucun membre. Cliquez "Ajouter un membre" pour commencer.</p>
              </div>
            ) : members.map((m, i) => (
              <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 group" data-testid={`regional-member-${i}`}>
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-lg font-bold overflow-hidden shadow-md">
                    {m.photo_url ? <img src={m.photo_url.startsWith('http') ? m.photo_url : `${process.env.REACT_APP_BACKEND_URL}${m.photo_url}`} alt={m.name} className="w-full h-full object-cover" /> : (m.name ? m.name[0] : <User size={20} />)}
                  </div>
                  <label className="absolute -bottom-1 -right-1 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors shadow-sm">
                    <Upload size={10} className="text-gray-500" />
                    <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) handlePhotoUpload(i, e.target.files[0]); }} />
                  </label>
                </div>

                {/* Fields */}
                <div className="flex-1 grid grid-cols-3 gap-2">
                  <input value={m.name} onChange={e => updateMember(i, 'name', e.target.value)}
                    placeholder="Dr Nom Prénom" className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#35AFE4] outline-none" data-testid={`member-name-${i}`} />
                  <div className="relative">
                    <input value={m.role} onChange={e => updateMember(i, 'role', e.target.value)}
                      list="regional-roles-list" placeholder="Rôle" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#35AFE4] outline-none" data-testid={`member-role-${i}`} />
                    <datalist id="regional-roles-list">
                      {ROLE_OPTIONS.map(r => <option key={r} value={r} />)}
                    </datalist>
                  </div>
                  <div className="flex gap-2">
                    <input value={m.email} onChange={e => updateMember(i, 'email', e.target.value)}
                      placeholder="email@..." className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#35AFE4] outline-none" />
                    <button onClick={() => removeMember(i)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Link */}
        <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100">
          <p className="text-xs font-semibold text-blue-800 mb-1">Lien vers les fiches annuaire</p>
          <p className="text-xs text-blue-600">Les membres du bureau régional sont automatiquement liés à leur fiche praticien dans l'annuaire si leur email correspond à un compte enregistré.</p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default RégionalPageManagement;
